import { useMemo, useState } from "react";

import { Alert, Button, Col, Row, Spin, Tag } from "antd";
import { useParams } from "react-router-dom";
import { User } from "lucide-react";
import WithAuth from "../../hoc/WithAuth";
import {
  useGetOrganizationDetail,
  useGetOrganizationDocuments,
} from "../../api/organization";
import { useGetBanks } from "../../api/banks";
import { InfoCard } from "../../components/shared/InfoCard";
import { InfoCard as MetaInfoCard } from "../../components/cards/info-card";
import {
  BankOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import DetailHeader from "../../components/shared/DetailHeader";
import { DataTable } from "../../components/ui/data-table";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import { Icons } from "../../assets/images/icons";
import {
  OrganizationBankAccount,
  OrganizationDetailPayload,
  OrganizationDocument,
  formatOrganizationAddress,
  organizationDetailFromResponse,
  resolveOrganizationContactPerson,
} from "../../types/organization";
import { OrganizationEditModal } from "./OrganizationEditPage";

function formatAuditDate(value?: string): string {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toISOString().slice(0, 10);
}

const OrganizationDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth() as AuthContextValue;
  const [editOpen, setEditOpen] = useState(false);

  const orgId = id ?? user?.mfiId;

  const { data: response, isLoading, isError, error } =
    useGetOrganizationDetail(orgId);

  const { data: documentsResponse, isLoading: documentsLoading } =
    useGetOrganizationDocuments(orgId);

  const { data: banks = [] } = useGetBanks({ activeOnly: false });

  const org: OrganizationDetailPayload | undefined = useMemo(
    () => organizationDetailFromResponse(response),
    [response],
  );

  const contactPerson = useMemo(
    () => resolveOrganizationContactPerson(org),
    [org],
  );

  const documents: OrganizationDocument[] = useMemo(() => {
    const fromOrg = org?.documents;
    if (fromOrg?.length) return fromOrg;
    const fromApi = documentsResponse?.data?.data;
    return Array.isArray(fromApi) ? fromApi : [];
  }, [org?.documents, documentsResponse]);

  const banksById = useMemo(
    () => new Map(banks.map((b) => [b.id, b.bankName])),
    [banks],
  );

  const bankAccountsWithNames = useMemo(() => {
    return (org?.bankAccounts ?? []).map((account) => ({
      ...account,
      bankName:
        account.bankName ??
        (account.bankId != null
          ? banksById.get(account.bankId)
          : undefined) ??
        "—",
    }));
  }, [org?.bankAccounts, banksById]);

  const bankColumns = [
    {
      title: "Bank name",
      dataIndex: "bankName" as const,
      key: "bankName",
      width: 160,
    },
    {
      title: "Account name",
      dataIndex: "accountName" as const,
      key: "accountName",
      width: 160,
    },
    {
      title: "Account number",
      dataIndex: "accountNumber" as const,
      key: "accountNumber",
      width: 140,
    },
    {
      title: "Branch",
      dataIndex: "accountBranch" as const,
      key: "accountBranch",
      width: 140,
    },
    {
      title: "Primary",
      key: "isPrimary",
      width: 90,
      render: (_: unknown, record: OrganizationBankAccount) =>
        record.isPrimary ? (
          <Tag color="blue">Primary</Tag>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      title: "Active",
      key: "isActive",
      width: 90,
      render: (_: unknown, record: OrganizationBankAccount) =>
        record.isActive ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
  ];

  const documentColumns = [
    {
      title: "File name",
      dataIndex: "name" as const,
      key: "name",
      render: (name: string | undefined) => name ?? "N/A",
    },
    {
      title: "Document type",
      dataIndex: "documentType" as const,
      key: "documentType",
      render: (type: string | undefined) => type ?? "N/A",
    },
  ];

  const systemMeta = [
    {
      title: "Created By",
      value: org?.createdBy ?? "N/A",
      icon: User,
    },
    {
      title: "Created At",
      value: formatAuditDate(org?.createdAt),
      icon: Icons.calendar03Icon,
    },
    {
      title: "Updated By",
      value: org?.updatedBy ?? "N/A",
      icon: User,
    },
    {
      title: "Updated At",
      value: formatAuditDate(org?.updatedAt),
      icon: Icons.arrowReloadIcon,
    },
  ];

  if (!orgId) {
    return (
      <div className="p-6 bg-gray-50 min-h-[50vh] flex items-center justify-center">
        <Alert
          type="warning"
          message="No organization id"
          description="Your account is not linked to an organization (mfiId)."
          showIcon
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 bg-gray-50 min-h-[50vh] flex items-center justify-center">
        <Spin size="large" tip="Loading organization…" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6 bg-gray-50">
        <Alert
          type="error"
          message="Could not load organization"
          description={
            error instanceof Error ? error.message : "Request failed."
          }
          showIcon
        />
      </div>
    );
  }

  const displayPhone = org?.phoneNumber ?? org?.phoneNo;

  return (
    <div className="p-6 bg-gray-50 space-y-6">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <DetailHeader
            title="Organization details"
            subtitle="Off-taker organization"
            extra={
              <Button
                type="primary"
                className="font-medium"
                onClick={() => setEditOpen(true)}
              >
                Edit Organization
              </Button>
            }
          />
        </Col>

        <Col xs={24} md={12} className="flex flex-col gap-6">
          <InfoCard
            title="General information"
            icon={<InfoCircleOutlined className="text-blue-600" />}
            items={[
              {
                label: "Name",
                value: org?.name,
                type: "tag",
              },
              {
                label: "Short name",
                value: org?.shortName,
              },
              {
                label: "Organization type",
                value: org?.type,
              },
              {
                label: "Email",
                value: org?.email,
              },
              {
                label: "Phone",
                value: displayPhone,
              },
            ]}
          />

          <InfoCard
            title="Contact person"
            icon={<UserOutlined className="text-blue-600" />}
            items={[
              {
                label: "Full name",
                value: contactPerson.fullName,
                type: "tag",
              },
              {
                label: "Email",
                value: contactPerson.email,
              },
              {
                label: "Phone",
                value: contactPerson.phoneNumber,
              },
              {
                label: "Username",
                value: contactPerson.username,
              },
            ]}
          />
        </Col>

        <Col xs={24} md={12} className="flex flex-col gap-6">
          <InfoCard
            title="Address"
            icon={<EnvironmentOutlined className="text-blue-600" />}
            items={[
              {
                label: "Full address",
                value: formatOrganizationAddress(org?.address),
              },
            ]}
          />

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex-1">
            <div className="px-6 py-4 border-b border-gray-100">
              <span className="font-semibold text-gray-800">
                <BankOutlined className="text-blue-600 mr-2" />
                Bank accounts
              </span>
            </div>
            <div className="px-2 pb-2">
              {bankAccountsWithNames.length > 0 ? (
                <DataTable
                  columns={bankColumns}
                  rowKey={(r) => String(r.id ?? r.bankId ?? r.accountNumber)}
                  data={bankAccountsWithNames}
                  emptyText="No bank accounts"
                />
              ) : (
                <div className="px-4 py-8 text-center text-gray-500">
                  No bank accounts on file.
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <div className="bg-white border border-[#E6EEF6] rounded-2xl p-6 w-full">
        <h2 className="text-xl font-semibold text-gray-900 mb-1">
          Legal Documents
        </h2>
        <p className="text-sm text-gray-500 mb-4">Documents</p>
        <Spin spinning={documentsLoading}>
          {documents.length > 0 ? (
            <DataTable
              columns={documentColumns}
              rowKey={(r) => String(r.id ?? r.name)}
              data={documents}
              emptyText="No documents"
            />
          ) : (
            <div className="rounded-xl border border-gray-100 bg-gray-50/50 px-6 py-10 text-center text-gray-500">
              <FileTextOutlined className="text-2xl text-gray-400 mb-2" />
              <p className="m-0">No legal documents on file.</p>
            </div>
          )}
        </Spin>
      </div>

      <div className="bg-white border border-[#E6EEF6] rounded-2xl p-6 w-full">
        <h2 className="text-base font-medium text-black mb-6">
          System Information
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6">
          {systemMeta.map((meta) => (
            <MetaInfoCard
              key={meta.title}
              title={meta.title}
              value={meta.value}
              icon={meta.icon}
            />
          ))}
        </div>
      </div>

      <OrganizationEditModal
        open={editOpen}
        hideModal={() => setEditOpen(false)}
        orgId={orgId}
        organization={org}
      />
    </div>
  );
};

export default WithAuth(OrganizationDetailPage);
